import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import {
  Plane,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Send,
  Info,
  X,
  Loader2,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  RefreshCw,
  Landmark,
  CreditCard,
  DollarSign,
  FileText,
  Building2,
  TrendingUp,
  Receipt,
  Tag,
  Upload,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import AttachmentPreviewModal from '../components/travel/AttachmentPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PAYMENT_STATUS_BADGES = {
  submitted: { label: 'Awaiting Approval', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  pending_approval: { label: 'Awaiting Approval', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  awaiting_approval: { label: 'Awaiting Approval', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  pending: { label: 'Approved – Payment Pending', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  payment_pending: { label: 'Approved – Payment Pending', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  processing: { label: 'Payment Processing', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  paid: { label: 'Paid / Disbursed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  disbursed: { label: 'Paid / Disbursed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rejected: { label: 'Rejected / Cancelled', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  cancelled: { label: 'Rejected / Cancelled', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
};

const StatusBadge = ({ status }) => {
  const normStatus = (status || 'pending').toLowerCase();
  const meta = PAYMENT_STATUS_BADGES[normStatus] || { label: status?.replace(/_/g, ' ') || 'Pending', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border tracking-wider ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

export default function FinanceTravelPayments() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');

  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({
    total_records: 0,
    pending_payment_count: 0,
    paid_count: 0,
    rejected_count: 0,
    total_advance_requested: 0,
    total_paid_amount: 0,
  });

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal States
  const [viewingRequest, setViewingRequest] = useState(null);
  const [payModalData, setPayModalData] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paid_amount: 0,
    payment_date: new Date().toISOString().slice(0, 10),
    payment_ref: '',
    payment_remarks: '',
  });

  // Bulk Upload Modal States
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const handleExportExcel = () => {
    let url = `${API_URL}/travel-desk/finance/travel-payments/export?status=${statusFilter}&payment_type=${paymentTypeFilter}&company_id=${selectedCompany}`;
    if (fromDate) url += `&from_date=${fromDate}`;
    if (toDate) url += `&to_date=${toDate}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    window.open(url, '_blank');
  };

  const handleFullReport = () => {
    window.open(`${API_URL}/travel-desk/finance/travel-payments/full-report?company_id=${selectedCompany}`, '_blank');
  };

  const handleDownloadBulkTemplate = () => {
    let url = `${API_URL}/travel-desk/finance/travel-payments/bulk-template?company_id=${selectedCompany}&payment_type=${paymentTypeFilter}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    window.open(url, '_blank');
  };

  const handleProcessBulkUpload = async () => {
    if (!bulkFile) {
      toast.error('Please select an Excel or CSV file to upload');
      return;
    }

    try {
      setBulkUploading(true);
      const formData = new FormData();
      formData.append('file', bulkFile);

      const { data } = await axios.post(
        `${API_URL}/travel-desk/finance/travel-payments/bulk-upload`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setBulkResult(data);
      if (data.successfully_updated > 0) {
        toast.success(`Successfully disbursed ${data.successfully_updated} payments!`);
      } else {
        toast.warning(data.message || 'No payment records updated.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process bulk payment upload');
    } finally {
      setBulkUploading(false);
    }
  };

  // Fetch Companies
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(res.data || []);
      } catch {
        // Fallback silently if unavailable
      }
    })();
  }, []);

  // Fetch Travel Payments Data
  const fetchTravelPayments = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/travel-desk/finance/travel-payments?status=${statusFilter}&payment_type=${paymentTypeFilter}&company_id=${selectedCompany}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const { data } = await axios.get(url, { withCredentials: true });
      const reqList = data?.data || data?.requests || (Array.isArray(data) ? data : []);
      setRequests(reqList);
      if (data?.summary) {
        setSummary(data.summary);
      }
    } catch (err) {
      toast.error('Failed to load Finance Travel Payments data');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, statusFilter, paymentTypeFilter, fromDate, toDate, search]);

  useEffect(() => {
    fetchTravelPayments();
  }, [fetchTravelPayments]);

  // Open View Request Modal (fetches enriched approval audit details)
  const handleViewClick = async (r) => {
    const reqId = r.id || r._id;
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/requests/${reqId}`, { withCredentials: true });
      setViewingRequest(data.request || r);
    } catch (err) {
      setViewingRequest(r);
    }
  };

  // Open Pay Modal
  const openPayModal = (req) => {
    const defaultAmount = req.is_self_booking
      ? (req.self_booking_details?.total_claim_amount || req.total_claim_amount || req.advance_amount || 0)
      : (req.approved_advance_amount ?? req.advance_details?.approved_advance_amount ?? req.finance_payable ?? req.advance_amount ?? 0);

    setPaymentForm({
      paid_amount: defaultAmount,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_ref: req.finance_payment_details?.utr_number || req.advance_payment_ref || '',
      payment_remarks: req.finance_payment_details?.remarks || 'Disbursed by Finance',
    });
    setPayModalData(req);
  };

  // Submit Payment Action (Paid / Rejected)
  const handleProcessPayment = async (targetStatus) => {
    if (!payModalData) return;

    if (targetStatus === 'paid') {
      if (!paymentForm.payment_ref.trim()) {
        toast.error('Please enter Payment Reference / UTR Number.');
        return;
      }
      if (!paymentForm.paid_amount || paymentForm.paid_amount <= 0) {
        toast.error('Please enter a valid Paid Amount.');
        return;
      }
    }

    try {
      setActionLoading(true);
      const payload = {
        advance_paid: targetStatus === 'paid',
        finance_payment_status: targetStatus,
        paid_amount: parseFloat(paymentForm.paid_amount) || 0,
        payment_date: paymentForm.payment_date,
        payment_ref: paymentForm.payment_ref.trim(),
        payment_remarks: paymentForm.payment_remarks.trim(),
      };

      const { data } = await axios.post(
        `${API_URL}/travel-desk/requests/${payModalData.id || payModalData._id}/advance-payment`,
        payload,
        { withCredentials: true }
      );

      if (data.success) {
        toast.success(data.message || `Travel payment status updated to ${targetStatus.toUpperCase()}!`);
        setPayModalData(null);
        fetchTravelPayments();
      } else {
        toast.error(data.message || 'Failed to update payment status');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to complete payment processing');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-7xl mx-auto pb-16 font-sans" data-testid="finance-travel-payments-page">
        
        {/* TOP HEADER */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-500/20 text-indigo-300 rounded-xl border border-indigo-500/30 shrink-0">
              <Plane className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight">Finance — Travel Payments</h1>
              <p className="text-xs text-indigo-200 mt-0.5">
                Process disbursements, manage travel advances &amp; record UTR references for approved Travel Desk requisitions.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="bg-slate-800 text-white border border-slate-700 rounded-xl px-3 py-2 text-xs font-bold outline-none cursor-pointer"
              data-testid="select-company-filter"
            >
              <option value="all">All Companies</option>
              {companies.map((c) => (
                <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>
              ))}
            </select>

            <button
              onClick={handleExportExcel}
              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 transition-all"
              title="Export Filtered Excel"
              data-testid="btn-export-excel"
            >
              <Download className="w-3.5 h-3.5" /> Export Excel
            </button>

            <button
              onClick={() => setShowBulkModal(true)}
              className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 transition-all"
              title="Bulk Payment Upload"
              data-testid="btn-bulk-upload"
            >
              <Upload className="w-3.5 h-3.5" /> Bulk Upload
            </button>

            <button
              onClick={handleFullReport}
              className="px-3.5 py-2 bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 transition-all"
              title="Full Travel Payment Report"
              data-testid="btn-full-report"
            >
              <FileText className="w-3.5 h-3.5" /> Full Report
            </button>

            <Button 
              onClick={fetchTravelPayments} 
              variant="outline" 
              className="text-xs border-slate-700 bg-slate-800 text-white hover:bg-slate-700"
              data-testid="btn-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>

        {/* SUMMARY KPI CARDS */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 text-xs" data-testid="summary-cards">
          <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Requisitions</span>
            <p className="text-xl font-black text-slate-800">{summary.total_records}</p>
          </div>
          <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-3.5 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Pending Payment</span>
            <p className="text-xl font-black text-amber-800">{summary.pending_payment_count}</p>
          </div>
          <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-3.5 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Paid / Disbursed</span>
            <p className="text-xl font-black text-emerald-800">{summary.paid_count}</p>
          </div>
          <div className="bg-rose-50/80 border border-rose-200 rounded-xl p-3.5 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wider">Payment Rejected</span>
            <p className="text-xl font-black text-rose-800">{summary.rejected_count}</p>
          </div>
          <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3.5 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider">Total Advances (₹)</span>
            <p className="text-lg font-black text-indigo-900 font-mono">
              ₹ {(summary.total_advance_requested || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="bg-emerald-100/60 border border-emerald-300 rounded-xl p-3.5 shadow-sm space-y-1">
            <span className="text-[10px] font-bold text-emerald-900 uppercase tracking-wider">Total Disbursed (₹)</span>
            <p className="text-lg font-black text-emerald-900 font-mono">
              ₹ {(summary.total_paid_amount || 0).toLocaleString('en-IN')}
            </p>
          </div>
        </div>

        {/* FILTERS & SEARCH */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3 text-xs" data-testid="finance-travel-filters">
          <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 flex-1 min-w-[220px]">
              <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <input
                type="text"
                placeholder="Search req #, employee name, destination..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-transparent outline-none text-xs w-full"
                data-testid="input-search"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-300 rounded-xl px-3 py-1.5 bg-white font-semibold outline-none cursor-pointer"
              data-testid="select-status-filter"
            >
              <option value="all">All Payment Statuses</option>
              <option value="pending">Pending Payment</option>
              <option value="paid">Paid / Disbursed</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={paymentTypeFilter}
              onChange={(e) => setPaymentTypeFilter(e.target.value)}
              className="border border-slate-300 rounded-xl px-3 py-1.5 bg-white font-semibold outline-none cursor-pointer text-xs"
              data-testid="select-type-filter"
            >
              <option value="all">All Payments &amp; Claims</option>
              <option value="advance">Travel Advances (Pre-Travel)</option>
              <option value="self_booking">Self Booking Claims (Reimbursements)</option>
            </select>
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <span className="text-slate-400 text-[11px] font-medium">Date Range:</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border border-slate-300 rounded-xl px-2.5 py-1 text-xs outline-none"
              data-testid="input-from-date"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border border-slate-300 rounded-xl px-2.5 py-1 text-xs outline-none"
              data-testid="input-to-date"
            />
          </div>
        </div>

        {/* MASTER TABLE */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="table-travel-payments">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-xs italic">
                No travel requests found matching the selected Finance filter.
              </div>
            ) : (
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2.5">Req # &amp; Date</th>
                    <th className="px-3 py-2.5">Employee</th>
                    <th className="px-3 py-2.5">Travel Title &amp; Destination</th>
                    <th className="px-3 py-2.5">Travel Dates</th>
                    <th className="px-3 py-2.5 text-right">Requested Adv. (₹)</th>
                    <th className="px-3 py-2.5 text-right">Approved / Payable (₹)</th>
                    <th className="px-3 py-2.5 text-right">Disbursed (₹)</th>
                    <th className="px-3 py-2.5 text-center">Payment Status</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {requests.map((r) => {
                    const reqId = r.id || r._id;
                    const isPending = (r.finance_payment_status || 'pending').toLowerCase() === 'pending';
                    const isPaid = (r.finance_payment_status || '').toLowerCase() === 'paid';
                    const hasAdvance = r.advance_requested && (r.advance_amount > 0 || r.requested_advance_amount > 0);

                    const reqAdvVal = r.requested_advance_amount || r.advance_details?.requested_advance_amount || r.advance_details?.total_advance || 0;
                    const appAdvVal = r.approved_advance_amount ?? r.advance_details?.approved_advance_amount ?? r.finance_payable ?? r.advance_amount ?? 0;

                    return (
                      <tr key={reqId} className="hover:bg-slate-50/80 transition" data-testid={`travel-row-${reqId}`}>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <p className="font-bold text-slate-900 font-mono">{r.request_number || r.req_number || `REQ-${reqId.slice(-6).toUpperCase()}`}</p>
                          <p className="text-[10px] text-slate-400">{(r.created_at || r.request_date || '').slice(0, 10)}</p>
                        </td>

                        <td className="px-3 py-3">
                          <p className="font-bold text-slate-900">{r.beneficiary_name || r.employee_name}</p>
                          <p className="text-[10px] text-slate-500 font-mono">{r.beneficiary_employee_code || r.employee_code || r.emp_code} &middot; {r.department || 'N/A'}</p>
                          {r.requested_on_behalf && (
                            <span className="text-[9px] bg-indigo-50 text-indigo-700 font-bold px-1.5 py-0.5 rounded border border-indigo-200 mt-0.5 inline-block">
                              Req by: {r.requester_name || r.employee_name}
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-3 max-w-xs">
                          <p className="font-semibold text-slate-800 truncate" title={r.trip_title || r.purpose}>
                            {r.trip_title || r.purpose || 'Travel Request'}
                          </p>
                          <p className="text-[10px] text-slate-500 truncate">
                            {r.origin_city || r.from_city || 'Origin'} &rarr; {r.destination_city || r.destination || 'Destination'}
                          </p>
                        </td>

                        <td className="px-3 py-3 whitespace-nowrap text-slate-700 font-medium">
                          <p>{r.start_date || r.departure_date} to</p>
                          <p>{r.end_date || r.return_date}</p>
                        </td>

                        <td className="px-3 py-3 text-right font-mono font-bold whitespace-nowrap">
                          {hasAdvance ? (
                            <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                              ₹ {(reqAdvVal || appAdvVal).toLocaleString('en-IN')}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-[10px]">No Advance</span>
                          )}
                        </td>

                        <td className="px-3 py-3 text-right font-mono font-black whitespace-nowrap">
                          {hasAdvance ? (
                            <span className="text-blue-900 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200">
                              ₹ {appAdvVal.toLocaleString('en-IN')}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-[10px]">&mdash;</span>
                          )}
                        </td>

                        <td className="px-3 py-3 text-right font-mono font-black whitespace-nowrap text-emerald-700">
                          {isPaid ? (
                            <div>
                              <p>₹ {(r.finance_payment_details?.paid_amount || r.advance_paid_amount || r.advance_amount || 0).toLocaleString('en-IN')}</p>
                              {r.finance_payment_details?.utr_number && (
                                <p className="text-[10px] text-slate-400 font-mono font-normal">UTR: {r.finance_payment_details.utr_number}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-[10px] font-normal">&mdash;</span>
                          )}
                        </td>

                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <StatusBadge status={r.finance_payment_status || 'pending'} />
                        </td>

                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleViewClick(r)}
                              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg flex items-center gap-1"
                              title="View full requisition details"
                              data-testid={`btn-view-${reqId}`}
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>

                            {isPending && (
                              <button
                                type="button"
                                onClick={() => openPayModal(r)}
                                className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg flex items-center gap-1 shadow-sm"
                                data-testid={`btn-pay-${reqId}`}
                              >
                                <CreditCard className="w-3.5 h-3.5" /> Mark Paid
                              </button>
                            )}

                            {isPaid && (
                              <button
                                type="button"
                                onClick={() => openPayModal(r)}
                                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold rounded-lg flex items-center gap-1"
                                title="Edit payment details / UTR"
                              >
                                <FileText className="w-3.5 h-3.5" /> Edit Payment
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* PROCESS / MARK AS PAID MODAL */}
        {payModalData && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="pay-modal">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl p-6 border border-slate-200 space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-indigo-600" /> Process Travel Payment
                </h3>
                <button type="button" onClick={() => setPayModalData(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Summary Box */}
              <div className="bg-indigo-50 p-3.5 rounded-xl border border-indigo-200 text-xs text-indigo-950 space-y-1">
                <div className="flex justify-between items-center font-bold">
                  <span>Req #: {payModalData.request_number || payModalData.req_number || payModalData.id}</span>
                  {payModalData.is_self_booking ? (
                    <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-purple-100 text-purple-800 border border-purple-300">Self Booking Reimbursement</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-indigo-100 text-indigo-800 border border-indigo-300">Travel Advance</span>
                  )}
                </div>
                <p><b>Employee:</b> {payModalData.employee_name} ({payModalData.employee_code || payModalData.emp_code})</p>
                {payModalData.advance_owner_name && (
                  <p><b>Advance Beneficiary Owner:</b> <span className="font-bold text-amber-900">{payModalData.advance_owner_name} ({payModalData.advance_owner_employee_id || 'N/A'})</span></p>
                )}
                <p><b>Destination:</b> {payModalData.destination_city || payModalData.destination || 'N/A'}</p>
                {payModalData.is_self_booking ? (
                  <p><b>Total Claimed Reimbursement:</b> <span className="font-bold text-purple-700 font-mono text-sm">₹ {(payModalData.self_booking_details?.total_claim_amount || payModalData.total_claim_amount || payModalData.advance_amount || 0).toLocaleString('en-IN')}</span></p>
                ) : (
                  payModalData.advance_requested && (
                    <div className="pt-1 space-y-0.5">
                      <p><b>Requested Advance:</b> <span className="font-bold text-amber-900 font-mono">₹ {(payModalData.requested_advance_amount || payModalData.advance_details?.requested_advance_amount || payModalData.advance_details?.total_advance || 0).toLocaleString('en-IN')}</span></p>
                      <p><b>Approved Advance / Finance Payable:</b> <span className="font-bold text-indigo-700 font-mono text-sm">₹ {(payModalData.approved_advance_amount ?? payModalData.advance_details?.approved_advance_amount ?? payModalData.finance_payable ?? payModalData.advance_amount ?? 0).toLocaleString('en-IN')}</span></p>
                    </div>
                  )
                )}
              </div>

              {/* Self Booking Itemized Breakdown if present */}
              {payModalData.is_self_booking && payModalData.self_booking_details && (
                <div className="bg-purple-50/80 p-3 rounded-xl border border-purple-200 text-[11px] space-y-2 text-purple-950">
                  <span className="font-bold text-purple-900 block uppercase tracking-wider">Itemized Expense Category Claim</span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div>Ticket: <b>₹ {(payModalData.self_booking_details.ticket_amount || 0).toLocaleString('en-IN')}</b></div>
                    <div>Hotel: <b>₹ {(payModalData.self_booking_details.hotel_amount || 0).toLocaleString('en-IN')}</b></div>
                    <div>Food: <b>₹ {(payModalData.self_booking_details.food_amount || 0).toLocaleString('en-IN')}</b></div>
                    <div>Local Travel: <b>₹ {(payModalData.self_booking_details.local_travel_amount || 0).toLocaleString('en-IN')}</b></div>
                    <div>Misc: <b>₹ {(payModalData.self_booking_details.misc_amount || 0).toLocaleString('en-IN')}</b></div>
                    <div>Total: <b className="font-mono text-purple-700">₹ {(payModalData.self_booking_details.total_claim_amount || 0).toLocaleString('en-IN')}</b></div>
                  </div>
                  {payModalData.self_booking_details.all_attachments?.length > 0 && (
                    <div className="pt-2 border-t border-purple-200 flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-700 text-[10px]">Attached Bills:</span>
                      {payModalData.self_booking_details.all_attachments.map((att, idx) => {
                        const isObj = att && typeof att === 'object';
                        const attName = isObj ? (att.name || att.filename || `Bill #${idx + 1}`) : (typeof att === 'string' && att.trim() ? att.split('?')[0].split('/').pop() || `Bill #${idx + 1}` : `Bill #${idx + 1}`);

                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setPreviewAttachment(att);
                              setIsPreviewOpen(true);
                            }}
                            className="px-2 py-0.5 bg-white border border-purple-300 rounded text-[10px] font-semibold text-purple-700 hover:bg-purple-50 transition-colors flex items-center gap-1 shadow-2xs"
                            title="Preview Attachment"
                          >
                            <Eye className="w-2.5 h-2.5" />
                            <span>{attName}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Advance Breakdown Summary if present */}
              {!payModalData.is_self_booking && payModalData.advance_requested && payModalData.advance_breakdown && (
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-[11px] grid grid-cols-2 gap-2 text-slate-700">
                  <div>Hotel: <b>₹ {(payModalData.advance_breakdown.hotel_amount || 0).toLocaleString('en-IN')}</b></div>
                  <div>Food: <b>₹ {(payModalData.advance_breakdown.food_amount || 0).toLocaleString('en-IN')}</b></div>
                  <div>Conveyance: <b>₹ {(payModalData.advance_breakdown.conveyance_amount || 0).toLocaleString('en-IN')}</b></div>
                  <div>Misc: <b>₹ {(payModalData.advance_breakdown.misc_amount || 0).toLocaleString('en-IN')}</b></div>
                </div>
              )}

              {/* Payment Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Disbursed Paid Amount (₹) *</label>
                  <input
                    type="number"
                    value={paymentForm.paid_amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paid_amount: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs font-mono font-bold"
                    data-testid="input-paid-amount"
                    required
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Disbursement Date *</label>
                  <input
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs font-semibold"
                    data-testid="input-paid-date"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block font-bold text-slate-700 mb-1">UTR / Bank Transaction Reference *</label>
                  <input
                    type="text"
                    placeholder="e.g. UTR123456789 / HDFC-NEFT-9921"
                    value={paymentForm.payment_ref}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_ref: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs font-mono uppercase"
                    data-testid="input-utr-ref"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Finance Payment Remarks</label>
                  <textarea
                    rows={2}
                    placeholder="Remarks regarding transfer batch or disbursement notes..."
                    value={paymentForm.payment_remarks}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_remarks: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl p-2.5 text-xs outline-none focus:border-indigo-500"
                    data-testid="input-payment-remarks"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => handleProcessPayment('rejected')}
                  disabled={actionLoading}
                  className="text-rose-600 hover:bg-rose-50 text-xs font-bold"
                >
                  Reject Payment
                </Button>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setPayModalData(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleProcessPayment('paid')}
                    disabled={actionLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                    data-testid="btn-confirm-pay"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Mark as Paid
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VIEW DETAILS MODAL */}
        {viewingRequest && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="view-travel-modal">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl space-y-4 p-6 border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    Travel Requisition #{viewingRequest.request_number || viewingRequest.req_number || viewingRequest.id}
                  </h3>
                  <p className="text-xs text-slate-500">{viewingRequest.trip_title || viewingRequest.purpose}</p>
                </div>
                <button type="button" onClick={() => setViewingRequest(null)} className="text-slate-400 hover:text-slate-600 p-1 rounded-md">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Key Highlights */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs">
                <div><span className="text-slate-400 block text-[10px]">Employee</span><b>{viewingRequest.employee_name} ({viewingRequest.employee_code || viewingRequest.emp_code})</b></div>
                <div><span className="text-slate-400 block text-[10px]">Department &amp; Project</span><b>{viewingRequest.department || 'N/A'} / {viewingRequest.project || 'N/A'}</b></div>
                <div><span className="text-slate-400 block text-[10px]">Travel Dates</span><b>{viewingRequest.start_date} to {viewingRequest.end_date}</b></div>
                <div><span className="text-slate-400 block text-[10px]">Primary Travel Mode</span><b className="capitalize">{viewingRequest.travel_mode || viewingRequest.primary_mode || 'N/A'}</b></div>
                <div><span className="text-slate-400 block text-[10px]">Overall Requisition Status</span><b className="uppercase">{viewingRequest.status}</b></div>
                <div><span className="text-slate-400 block text-[10px]">Finance Payment Status</span><StatusBadge status={viewingRequest.finance_payment_status || 'pending'} /></div>
              </div>

              {/* Self Booking Details */}
              {viewingRequest.is_self_booking || viewingRequest.self_booking_details ? (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-2 text-xs text-purple-950">
                  <div className="flex items-center justify-between">
                    <span className="font-bold uppercase tracking-wider text-purple-900">Self Booking Reimbursement Claim Breakdown</span>
                    <span className="font-black font-mono text-purple-900 text-sm">₹ {(viewingRequest.self_booking_details?.total_claim_amount || viewingRequest.total_claim_amount || viewingRequest.advance_amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                  {viewingRequest.self_booking_details && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 border-t border-purple-200 text-slate-700">
                      <div>Ticket: <b>₹ {(viewingRequest.self_booking_details.ticket_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Hotel: <b>₹ {(viewingRequest.self_booking_details.hotel_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Food: <b>₹ {(viewingRequest.self_booking_details.food_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Local Travel: <b>₹ {(viewingRequest.self_booking_details.local_travel_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Misc: <b>₹ {(viewingRequest.self_booking_details.misc_amount || 0).toLocaleString('en-IN')}</b></div>
                    </div>
                  )}
                  {viewingRequest.self_booking_details?.all_attachments?.length > 0 && (
                    <div className="pt-2 border-t border-purple-200 flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-700 text-[10px]">Submitted Invoice Bills:</span>
                      {viewingRequest.self_booking_details.all_attachments.map((att, idx) => {
                        const isObj = att && typeof att === 'object';
                        const attName = isObj ? (att.name || att.filename || `Bill #${idx + 1}`) : (typeof att === 'string' && att.trim() ? att.split('?')[0].split('/').pop() || `Bill #${idx + 1}` : `Bill #${idx + 1}`);

                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setPreviewAttachment(att);
                              setIsPreviewOpen(true);
                            }}
                            className="px-2 py-0.5 bg-white border border-purple-300 rounded text-[10px] font-semibold text-purple-700 hover:bg-purple-50 transition-colors flex items-center gap-1 shadow-2xs"
                            title="Preview Attachment"
                          >
                            <Eye className="w-2.5 h-2.5" />
                            <span>{attName}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : viewingRequest.advance_requested ? (
                <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-4 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-amber-900 uppercase tracking-wider">Advance Estimation Breakdown</span>
                    <span className="font-black text-amber-950 font-mono text-sm">₹ {(viewingRequest.advance_amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                  {viewingRequest.advance_owner_name && (
                    <div className="text-[11px] text-amber-950 bg-white/80 p-2 rounded-lg border border-amber-300 flex justify-between items-center font-medium">
                      <span>Advance Beneficiary Owner:</span>
                      <span className="font-bold font-mono">{viewingRequest.advance_owner_name} ({viewingRequest.advance_owner_employee_id || 'N/A'})</span>
                    </div>
                  )}
                  {viewingRequest.advance_breakdown && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 border-t border-amber-200/60 text-slate-700">
                      <div>Hotel: <b>₹ {(viewingRequest.advance_breakdown.hotel_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Food: <b>₹ {(viewingRequest.advance_breakdown.food_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Local Travel: <b>₹ {(viewingRequest.advance_breakdown.conveyance_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Misc: <b>₹ {(viewingRequest.advance_breakdown.misc_amount || 0).toLocaleString('en-IN')}</b></div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-500 italic">
                  No advance payment was requested by the employee for this travel requisition.
                </div>
              )}

              {/* Disbursement Record if Paid */}
              {viewingRequest.finance_payment_details && viewingRequest.finance_payment_status === 'paid' && (
                <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-4 space-y-1 text-xs text-emerald-950">
                  <p className="font-bold text-emerald-900 text-sm flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Recorded Payment Disbursement
                  </p>
                  <div className="grid grid-cols-2 gap-2 pt-1 font-medium">
                    <div>Paid Amount: <b>₹ {(viewingRequest.finance_payment_details.paid_amount || 0).toLocaleString('en-IN')}</b></div>
                    <div>Payment Date: <b>{viewingRequest.finance_payment_details.payment_date || 'N/A'}</b></div>
                    <div>UTR / Reference: <b className="font-mono">{viewingRequest.finance_payment_details.utr_number || 'N/A'}</b></div>
                    <div>Disbursed By: <b>{viewingRequest.finance_payment_details.paid_by_name || 'Finance Admin'}</b></div>
                  </div>
                  {viewingRequest.finance_payment_details.remarks && (
                    <p className="text-emerald-700 italic pt-1">Remarks: {viewingRequest.finance_payment_details.remarks}</p>
                  )}
                </div>
              )}

              {/* APPROVAL HISTORY & AUDIT TRAIL */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 text-xs" data-testid="view-approval-history-section">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <h4 className="font-extrabold text-blue-900 text-sm flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-blue-600" /> APPROVAL HISTORY &amp; AUDIT TRAIL
                  </h4>
                  {(viewingRequest.payment_processed_by || viewingRequest.finance_audit?.payment_processed_by?.display) && (
                    <span className="text-[11px] font-bold text-indigo-950 bg-indigo-50 px-2.5 py-0.5 rounded border border-indigo-200">
                      Payment Processed By: <b>{viewingRequest.payment_processed_by || viewingRequest.finance_audit?.payment_processed_by?.display}</b>
                    </span>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse border border-slate-200 rounded-xl overflow-hidden">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                        <th className="p-2">Level</th>
                        <th className="p-2">Approval Role / Stage</th>
                        <th className="p-2">Approved By (Employee Code)</th>
                        <th className="p-2">Status / Action</th>
                        <th className="p-2">Action Date &amp; Time</th>
                        <th className="p-2">Remarks / Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-medium">
                      {((viewingRequest.approval_history && viewingRequest.approval_history.length > 0)
                        ? viewingRequest.approval_history
                        : ((viewingRequest.approvals && viewingRequest.approvals.length > 0)
                            ? viewingRequest.approvals
                            : (viewingRequest.approval_timeline || []))
                      ).map((lvl, idx) => {
                        const st = String(lvl.action || lvl.status || 'PENDING').toUpperCase();
                        const rawTime = lvl.approved_at || lvl.decided_at || lvl.timestamp;
                        const timeStr = rawTime ? String(rawTime).replace('T', ' ').slice(0, 19) : '—';
                        const approverDisp = lvl.approver_name || lvl.actor_name || lvl.approver_role || 'Approver';
                        return (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="p-2 font-bold text-slate-700">{lvl.level || idx + 1}</td>
                            <td className="p-2 font-bold text-slate-900">{lvl.role || lvl.level_name || lvl.step || `Level ${idx + 1}`}</td>
                            <td className="p-2 font-bold text-blue-950">{approverDisp}</td>
                            <td className="p-2">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold uppercase border ${
                                st === 'APPROVED'
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                  : st === 'PENDING'
                                  ? 'bg-blue-100 text-blue-800 border-blue-300 font-extrabold'
                                  : st === 'REJECTED'
                                  ? 'bg-rose-100 text-rose-800 border-rose-300'
                                  : st === 'SENT_BACK' || st === 'SENT BACK'
                                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                                  : 'bg-slate-100 text-slate-600 border-slate-200'
                              }`}>
                                {st}
                              </span>
                            </td>
                            <td className="p-2 font-mono text-slate-600 text-[11px]">{timeStr}</td>
                            <td className="p-2 text-slate-600">{lvl.comments || lvl.remarks || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={() => setViewingRequest(null)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* BULK UPLOAD MODAL */}
        {showBulkModal && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="modal-bulk-upload">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-5 border border-slate-200">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <div>
                  <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                    <Upload className="w-5 h-5 text-indigo-600" /> Bulk Travel Payment Disbursement Upload
                  </h3>
                  <p className="text-xs text-slate-500">Download pending payment template, update UTR references, and process bulk disbursement.</p>
                </div>
                <button
                  onClick={() => { setShowBulkModal(false); setBulkFile(null); setBulkResult(null); }}
                  className="text-slate-400 hover:text-slate-600 font-bold p-1 rounded-lg"
                  data-testid="btn-close-bulk-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Step 1: Download Template */}
              <div className="p-4 bg-indigo-50/60 rounded-xl border border-indigo-100 flex items-center justify-between gap-4">
                <div className="space-y-0.5 text-xs">
                  <div className="font-bold text-indigo-900">Step 1: Download Pending Payments Template</div>
                  <div className="text-slate-600 text-[11px]">Includes all UNPAID / PENDING travel requisitions matching current scope. Paid items are excluded.</div>
                </div>
                <button
                  onClick={handleDownloadBulkTemplate}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 shrink-0 transition-all"
                  data-testid="btn-download-template"
                >
                  <Download className="w-3.5 h-3.5" /> Download Template
                </button>
              </div>

              {/* Step 2: Select File */}
              <div className="space-y-2 text-xs">
                <label className="block font-bold text-slate-700">Step 2: Upload Completed Excel (.xlsx) or CSV File</label>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                  className="w-full text-xs text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-800 hover:file:bg-slate-200 cursor-pointer border border-slate-200 rounded-xl p-2"
                  data-testid="input-bulk-file"
                />
              </div>

              {/* Step 3: Action Buttons */}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => { setShowBulkModal(false); setBulkFile(null); setBulkResult(null); }}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!bulkFile || bulkUploading}
                  onClick={handleProcessBulkUpload}
                  className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs rounded-xl shadow-md flex items-center gap-2 disabled:opacity-50"
                  data-testid="btn-submit-bulk-upload"
                >
                  {bulkUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {bulkUploading ? 'Processing Upload...' : 'Validate & Disburse Payments'}
                </button>
              </div>

              {/* Result Summary Box */}
              {bulkResult && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 text-xs" data-testid="bulk-upload-results">
                  <div className="font-bold text-slate-900 border-b border-slate-200 pb-2 flex items-center justify-between">
                    <span>Bulk Upload Result Summary</span>
                    <span className="text-emerald-700 font-bold">Successfully Updated: {bulkResult.successfully_updated}</span>
                  </div>

                  <div className="grid grid-cols-4 gap-2 text-center text-[11px] font-bold">
                    <div className="p-2 bg-white rounded-lg border border-slate-200">
                      <div className="text-slate-400">Total Uploaded</div>
                      <div className="text-sm text-slate-800">{bulkResult.total_uploaded}</div>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="text-emerald-700">Disbursed</div>
                      <div className="text-sm text-emerald-800">{bulkResult.successfully_updated}</div>
                    </div>
                    <div className="p-2 bg-rose-50 rounded-lg border border-rose-200">
                      <div className="text-rose-700">Failed / Errors</div>
                      <div className="text-sm text-rose-800">{bulkResult.failed_count}</div>
                    </div>
                    <div className="p-2 bg-slate-100 rounded-lg border border-slate-200">
                      <div className="text-slate-600">Skipped</div>
                      <div className="text-sm text-slate-800">{bulkResult.skipped_count}</div>
                    </div>
                  </div>

                  {/* Failed Records Details Table */}
                  {bulkResult.errors && bulkResult.errors.length > 0 && (
                    <div className="space-y-1.5 pt-2">
                      <div className="font-bold text-rose-800 text-[11px]">Validation &amp; Processing Failures ({bulkResult.errors.length}):</div>
                      <div className="max-h-40 overflow-y-auto border border-rose-200 rounded-xl bg-rose-50/40 p-2 space-y-1 text-[11px]">
                        {bulkResult.errors.map((err, idx) => (
                          <div key={idx} className="flex gap-2 text-rose-900 font-medium">
                            <span className="font-bold text-rose-700">Row {err.row} ({err.request_number}):</span>
                            <span>{err.reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Attachment Preview Modal */}
      <AttachmentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        attachment={previewAttachment}
        title="Payment Bill &amp; Receipt Preview"
      />
    </MainLayout>
  );
}
